import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/Admin/AdminLayout';
import Dashboard from '../components/Admin/Dashboard';
import ArticlesManager from '../components/Admin/ArticlesManager';
import ArticleForm from '../components/Admin/ArticleForm';
import VideosManager from '../components/Admin/VideosManager';
import VideoForm from '../components/Admin/VideoForm';
import SectionsManager from '../components/Admin/SectionsManager';
import SettingsManager from '../components/Admin/SettingsManager';
import { useApp } from '../context/AppContext';
import JokesManager from '../components/Admin/JokesManager';
import api from '../services/api';

const AdminPage = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');
    const { showToast } = useApp();
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            verifyToken(token);
        }
    }, []);

    const verifyToken = async (token) => {
        try {
            const response = await api.get('/auth/me');
            if (response.data.success) {
                setIsAuthenticated(true);
            } else {
                localStorage.removeItem('adminToken');
            }
        } catch (error) {
            localStorage.removeItem('adminToken');
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setLoginError('');

        try {
            const response = await api.post('/auth/login', {
                email: loginEmail,
                password: loginPassword
            });

            if (response.data.success) {
                localStorage.setItem('adminToken', response.data.token);
                setIsAuthenticated(true);
                showToast('Login successful!');
                navigate('/admin/dashboard');
            } else {
                setLoginError(response.data.message || 'Invalid credentials');
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginError(error.response?.data?.message || 'Login failed. Please try again.');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        showToast('Logged out successfully');
        navigate('/admin');
    };

    if (!isAuthenticated) {
        return (
            <div style={{ minHeight: '100vh', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,.1)', width: '100%', maxWidth: '400px' }}>
                    {/* Logo + Name on Login Page */}
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '10px' }}>
                            <img
                                src="/logo.png"
                                alt="Wistar News"
                                style={{ height: '45px', width: 'auto' }}
                                onError={(e) => e.target.style.display = 'none'}
                            />
                            <div style={{ fontFamily: 'var(--ff-label)', fontSize: '28px', color: 'var(--navy)' }}>
                                Wistar<span style={{ color: 'var(--red)' }}>News</span>
                            </div>
                        </div>
                        <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>Admin Login</p>
                    </div>

                    {loginError && (
                        <div style={{
                            background: '#ffe0e0',
                            color: 'var(--red)',
                            padding: '10px',
                            borderRadius: '5px',
                            marginBottom: '16px',
                            fontSize: '13px',
                            textAlign: 'center'
                        }}>
                            {loginError}
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Email</label>
                            <input
                                type="email"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                                placeholder="admin@wistarnews.com"
                                required
                            />
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Password</label>
                            <input
                                type="password"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                                placeholder="Enter your password"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'var(--red)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                fontWeight: '600',
                                cursor: loginLoading ? 'not-allowed' : 'pointer',
                                opacity: loginLoading ? 0.7 : 1
                            }}
                            disabled={loginLoading}
                        >
                            {loginLoading ? 'Logging in...' : 'Login'}
                        </button>
                    </form>
                    <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#888' }}>
                        Default: admin@wistarnews.com / admin123
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
            <div style={{ background: 'var(--navy)', color: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', height: '56px', gap: '0', position: 'sticky', top: 0, zIndex: 1000 }}>
                {/* Logo + Name Side by Side in Admin Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '32px', flexShrink: 0 }}>
                    <img
                        src="/logo.png"
                        alt="Wistar News"
                        style={{ height: '32px', width: 'auto', backgroundColor: 'white', borderRadius: '4px', padding: '2px' }}
                        onError={(e) => e.target.style.display = 'none'}
                    />
                    <div style={{ fontFamily: 'var(--ff-label)', fontSize: '20px', letterSpacing: '1px' }}>
                        Wistar<span style={{ color: 'var(--red)' }}>News</span>
                    </div>
                    <div style={{
                        fontSize: '11px',
                        background: 'rgba(255, 255, 255, 0.15)',
                        padding: '2px 8px',
                        borderRadius: '20px',
                        marginLeft: '5px'
                    }}>
                        Admin
                    </div>
                </div>
                <div style={{ display: 'flex', flex: 1, overflowX: 'auto' }}>
                    <Link to="/admin/dashboard" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>📊 Dashboard</Link>
                    <Link to="/admin/articles" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>📰 Articles</Link>
                    <Link to="/admin/add-article" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>✍️ Add Article</Link>
                    <Link to="/admin/videos" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>📹 Videos</Link>
                    <Link to="/admin/add-video" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>➕ Add Video</Link>
                    <Link to="/admin/sections" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>🗂 Sections</Link>
                    <Link to="/admin/settings" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>⚙️ Settings</Link>
                    <Link to="/admin/jokes" style={{ padding: '0 18px', height: '56px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '600', color: '#8bafc8', borderBottom: '3px solid transparent', textDecoration: 'none' }}>😂 Jokes</Link>
                </div>
                <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', padding: '7px 16px', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', flexShrink: 0, marginLeft: 'auto' }}>← Logout</button>
            </div>

            <div style={{ maxWidth: '1180px', margin: '0 auto', padding: '28px 20px' }}>
                <Routes>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="articles" element={<ArticlesManager />} />
                    <Route path="add-article" element={<ArticleForm />} />
                    <Route path="edit-article/:id" element={<ArticleForm />} />
                    <Route path="videos" element={<VideosManager />} />
                    <Route path="add-video" element={<VideoForm />} />
                    <Route path="edit-video/:id" element={<VideoForm />} />
                    <Route path="sections" element={<SectionsManager />} />
                    <Route path="settings" element={<SettingsManager />} />
                    <Route path="/" element={<Dashboard />} />
                    <Route path="jokes" element={<JokesManager />} />
                </Routes>
            </div>
        </div>
    );
};

export default AdminPage;